import { Badge, Dropdown, Table, Tooltip, useDrawer } from '@iveco-ti/iveco-ds'
import {
  Building2,
  EllipsisVertical,
  FileEdit,
  FileSearch,
  KeyRound,
  Server,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useActiveGeneralSettings } from '@/common/hooks/useActiveGeneralSettings'
import { ApplicationPaginated } from '../hooks/usePaginateApplications'
import { useRemoveApplication } from '../hooks/useRemoveApplication'
import { useManageApplicationStore } from '../stores/useManageApplicationStore'
import { useSelectedApplicationStore } from '../stores/useSelectedApplicationStore'

type ApplicationTableRowProps = {
  application: ApplicationPaginated
}

export function ApplicationTableRow({ application }: ApplicationTableRowProps) {
  const { t } = useTranslation(['common', 'sso'])
  const { handleRemoveApplication } = useRemoveApplication()
  const { open: openView } = useDrawer('drawer-view-application')
  const { open: openEdit } = useDrawer('drawer-edit-application')
  const { open: openAuth } = useDrawer('drawer-manage-auth')
  const { open: openManageApi } = useDrawer('drawer-manage-api-usage')
  const { open: openManageAccount } = useDrawer('drawer-manage-entity-account')
  const { setApplication } = useSelectedApplicationStore()
  const { setApplication: setManageApplication } = useManageApplicationStore()
  const { settings } = useActiveGeneralSettings()

  const handleViewApplication = () => {
    setApplication(application)
    openView()
  }

  const handleEditApplication = () => {
    setApplication(application)
    openEdit()
  }

  const handleManageAuth = () => {
    setManageApplication(application)
    openAuth()
  }

  const handleManageApiUsage = () => {
    setApplication(application)
    openManageApi()
  }

  const handleManageEntityAccount = () => {
    setApplication(application)
    openManageAccount()
  }

  return (
    <Table.Row hideCheckbox id={application?.id}>
      <Table.Data className="flex flex-col justify-center">
        <span className="text-base-800 uppercase font-medium text-sm">
          {application?.title}
        </span>
        <span className="text-base-500 uppercase font-light text-sm">
          {application?.applicationKey}
        </span>
      </Table.Data>

      <Table.Data className="flex flex-col justify-center">
        <span className="text-base-800 font-light text-sm">
          {application?.description || (
            <span className="text-base-400 font-light text-sm">
              {t('common:components.not_informed')}
            </span>
          )}
        </span>
      </Table.Data>

      <Table.Data className="flex flex-wrap gap-1">
        {application?.tag?.length > 0 ? (
          application.tag.map((tag) => (
            <Badge.Root
              key={tag}
              shape="square"
              colors="brand"
              className="text-xs"
            >
              {tag}
            </Badge.Root>
          ))
        ) : (
          <span className="text-base-400 font-light text-sm">
            {t('common:components.not_informed')}
          </span>
        )}
      </Table.Data>

      <Table.Data end>
        <div className="flex flex-row gap-2 items-start justify-center">
          <Tooltip text={t('common:components.table_row.view')}>
            <button type="button" onClick={handleViewApplication}>
              <FileSearch size={18} className="text-brand-700" />
            </button>
          </Tooltip>

          <Tooltip text={t('common:components.table_row.edit')}>
            <button type="button" onClick={handleEditApplication}>
              <FileEdit size={18} className="text-brand-700" />
            </button>
          </Tooltip>

          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <EllipsisVertical className="text-brand-700" size={18} />
            </Dropdown.Trigger>

            <Dropdown.Body>
              <Dropdown.List>
                {application.requiresApiUser && (
                  <Dropdown.Item
                    onClick={handleManageAuth}
                    className="text-brand-700 text-sm font-normal"
                  >
                    <Dropdown.Icon icon={<KeyRound />} />
                    {t(
                      'sso:applications.table.table_row.dropdown.authentication',
                    )}
                  </Dropdown.Item>
                )}

                <Dropdown.Item
                  onClick={handleManageApiUsage}
                  className="text-brand-700 text-sm font-normal"
                >
                  <Dropdown.Icon icon={<Server />} />
                  {t(
                    'sso:applications.table.table_row.dropdown.manage_api_usage',
                  )}
                </Dropdown.Item>

                {settings?.enableAccountEntity && (
                  <Dropdown.Item
                    onClick={handleManageEntityAccount}
                    className="text-brand-700 text-sm font-normal"
                  >
                    <Dropdown.Icon icon={<Building2 />} />
                    {t(
                      'sso:applications.table.table_row.dropdown.manage_entity_account',
                    )}
                  </Dropdown.Item>
                )}

                <Dropdown.Item
                  onClick={() => handleRemoveApplication(application.id)}
                  className="text-danger-600 text-sm font-normal"
                >
                  <Dropdown.Icon icon={<Trash2 />} />
                  {t(
                    'sso:applications.table.table_row.dropdown.remove_application',
                  )}
                </Dropdown.Item>
              </Dropdown.List>
            </Dropdown.Body>
          </Dropdown.Root>
        </div>
      </Table.Data>
    </Table.Row>
  )
}
